import React, { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const STATUS_LABELS = {
  pending: 'Menunggu',
  approved: 'Disetujui',
  rejected: 'Ditolak'
};

export default function AdminStories() {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    city: '',
    story: '',
    mediaUrl: '',
    status: 'pending'
  });

  const fetchStories = async () => {
    const snap = await getDocs(query(collection(db, 'stories'), orderBy('createdAt', 'desc')));
    setStories(snap.docs.map(docItem => ({ id: docItem.id, ...docItem.data() })));
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'stories', id), { status, reviewedAt: serverTimestamp() });
    await fetchStories();
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      city: item.city || '',
      story: item.story || '',
      mediaUrl: item.mediaUrl || '',
      status: item.status || 'pending'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    await updateDoc(doc(db, 'stories', editingId), {
      name: form.name.trim(),
      city: form.city.trim(),
      story: form.story.trim(),
      mediaUrl: form.mediaUrl.trim(),
      status: form.status,
      reviewedAt: serverTimestamp()
    });
    setEditingId(null);
    setForm({ name: '', city: '', story: '', mediaUrl: '', status: 'pending' });
    await fetchStories();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: '', city: '', story: '', mediaUrl: '', status: 'pending' });
  };

  const removeStory = async (id) => {
    if (!window.confirm('Hapus cerita ini?')) return;
    await deleteDoc(doc(db, 'stories', id));
    await fetchStories();
  };

  const filtered = stories
    .filter(item => filter === 'all' || item.status === filter)
    .filter(item =>
      item.story?.toLowerCase().includes(search.toLowerCase()) ||
      item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.city?.toLowerCase().includes(search.toLowerCase())
    );

  const isAll = pageSize === 'all';
  const totalItems = filtered.length;
  const totalPages = isAll ? 1 : Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = isAll ? 0 : (currentPage - 1) * pageSize;
  const endIndex = isAll ? totalItems : startIndex + pageSize;
  const displayed = isAll ? filtered : filtered.slice(startIndex, endIndex);

  return (
    <div className="admin-page admin-stories-page">
      <div className="admin-page-header">
        <div>
          <h2>Berbagi Cerita</h2>
          <p className="text-muted-small">Kelola cerita yang masuk dari pengunjung.</p>
        </div>
      </div>

      <div className="admin-card">
        <h3>{editingId ? 'Edit Cerita' : 'Ringkas Cerita'}</h3>
        <form className="testimonial-form" onSubmit={handleEditSubmit}>
          <input
            type="text"
            placeholder="Nama"
            value={form.name}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Kota"
            value={form.city}
            onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))}
          />
          <textarea
            rows="4"
            placeholder="Cerita"
            value={form.story}
            onChange={e => setForm(prev => ({ ...prev, story: e.target.value }))}
          />
          <input
            type="text"
            placeholder="URL Media (opsional)"
            value={form.mediaUrl}
            onChange={e => setForm(prev => ({ ...prev, mediaUrl: e.target.value }))}
          />
          <div className="admin-toolbar-group">
            <label className="text-muted-small">Status</label>
            <select
              className="form-select w-auto"
              value={form.status}
              onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="pending">Menunggu</option>
              <option value="approved">Disetujui</option>
              <option value="rejected">Ditolak</option>
            </select>
          </div>
          <div className="admin-form-actions">
            <button className="btn btn-primary" type="submit" disabled={!editingId}>
              Simpan Perubahan
            </button>
            {editingId && (
              <button type="button" className="btn btn-outline-secondary" onClick={cancelEdit}>
                Batal
              </button>
            )}
          </div>
        </form>
        {!editingId && (
          <p className="text-muted-small mb-0">
            Klik tombol “Edit” pada tabel untuk mengubah cerita, lalu simpan di sini.
          </p>
        )}
      </div>

      <div className="admin-toolbar">
        <select
          className="form-select w-auto"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        >
          <option value="pending">Menunggu</option>
          <option value="approved">Disetujui</option>
          <option value="rejected">Ditolak</option>
          <option value="all">Semua</option>
        </select>

        <input
          type="text"
          className="form-control w-auto"
          placeholder="Cari nama/kota/cerita"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <div className="admin-toolbar-group">
          <label className="text-muted-small">Tampil</label>
          <select
            className="form-select w-auto"
            value={pageSize}
            onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">Semua</option>
          </select>
        </div>
      </div>

      <div className="admin-pagination-info">
        Menampilkan {totalItems === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} cerita
      </div>

      <div className="table-responsive admin-table-wrapper">
        <table className="table admin-table align-middle admin-story-table">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Nama</th>
              <th>Kota</th>
              <th>Status</th>
              <th>Cerita</th>
              <th>Media</th>
              <th style={{ width: 180 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(item => (
              <tr key={item.id}>
                <td className="admin-id" title={item.id}>
                  {item.id.slice(0, 8)}
                </td>
                <td>{item.name || 'Hamba Allah'}</td>
                <td>{item.city || '-'}</td>
                <td>
                  <span
                    className={`badge bg-${
                      item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'secondary' : 'warning'
                    }`}
                  >
                    {STATUS_LABELS[item.status] || 'Menunggu'}
                  </span>
                </td>
                <td className="admin-story-text" title={item.story}>
                  {(item.story || '').slice(0, 120)}{item.story && item.story.length > 120 ? '...' : ''}
                </td>
                <td>
                  {item.mediaUrl ? (
                    <a href={item.mediaUrl} target="_blank" rel="noreferrer">Lihat</a>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <div className="btn-group btn-group-sm">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => startEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-outline-success"
                      onClick={() => updateStatus(item.id, 'approved')}
                    >
                      Terima
                    </button>
                    <button
                      className="btn btn-outline-warning"
                      onClick={() => updateStatus(item.id, 'rejected')}
                    >
                      Tolak
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => removeStory(item.id)}
                    >
                      Hapus
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {displayed.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center">
                  Tidak ada cerita ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isAll && totalPages > 1 && (
        <div className="pagination-footer admin-pagination-footer">
          <button
            className="btn btn-outline-success btn-sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          >
            &lt; Sebelumnya
          </button>
          <select
            className="form-select form-select-sm page-select"
            value={currentPage}
            onChange={e => setCurrentPage(Number(e.target.value))}
          >
            {Array.from({ length: totalPages }).map((_, idx) => (
              <option key={idx + 1} value={idx + 1}>
                Halaman {idx + 1} / {totalPages}
              </option>
            ))}
          </select>
          <button
            className="btn btn-outline-success btn-sm"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          >
            Berikutnya &gt;
          </button>
        </div>
      )}
    </div>
  );
}
